import { openDB } from 'idb'
import { IndexedBlobLike, toReadableBlob, toStorableBlobPayload } from '@/utils/indexedBlob'

export const WHATSHOT_DB_NAME = 'WhatshotHandoffDB'
// The transfers store is schemaless, so adding transcript fields requires no
// IndexedDB schema migration.
export const WHATSHOT_DB_VERSION = 1
export const WHATSHOT_TRANSFERS_STORE = 'transfers'
export const WHATSHOT_TRANSFER_QUERY_PARAM = 'whatshotTransfer'

export const WHATSHOT_MAX_PRODUCTS = 500
export const WHATSHOT_MAX_FRAMES_PER_PRODUCT = 8
export const WHATSHOT_ACCEPTED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const
export const WHATSHOT_MAX_BLOB_BYTES = 10 * 1024 * 1024
export const WHATSHOT_MAX_BATCH_BYTES = 100 * 1024 * 1024

export interface WhatshotFrameGroup {
  label: string
  frames: Blob[]
  /** Source-video timestamps in seconds, when supplied by current WhatShot. */
  start?: number
  end?: number
}

export interface WhatshotTranscriptSegment {
  start: number
  end: number
  text: string
}

export interface WhatshotTranscript {
  language: 'en'
  segments: WhatshotTranscriptSegment[]
}

export interface WhatshotTransferRecord {
  id: string
  groups: WhatshotFrameGroup[]
  totalProductCount: number
  transcript?: WhatshotTranscript
  ts: number
}

interface RawWhatshotFrameGroup {
  label: string
  frames: IndexedBlobLike[]
  frameTypes: string[]
  start?: number
  end?: number
}

interface RawWhatshotTransferRecord {
  id: string
  groups: RawWhatshotFrameGroup[]
  totalProductCount: number
  transcript?: WhatshotTranscript
  ts: number
}

export interface SaveWhatshotTransferPayload {
  transferId: string
  groups: WhatshotFrameGroup[]
  totalProductCount: number
  transcript?: WhatshotTranscript
  ts?: number
}

async function openWhatshotDB() {
  return openDB(WHATSHOT_DB_NAME, WHATSHOT_DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(WHATSHOT_TRANSFERS_STORE)) {
        db.createObjectStore(WHATSHOT_TRANSFERS_STORE, { keyPath: 'id' })
      }
    },
  })
}

function normalizeTransferRecord(
  record: RawWhatshotTransferRecord | null | undefined,
): WhatshotTransferRecord | null {
  if (!record) return null

  const groups: WhatshotFrameGroup[] = []
  for (const group of record.groups ?? []) {
    const frames: Blob[] = []
    for (let index = 0; index < (group.frames?.length ?? 0); index += 1) {
      const frame = toReadableBlob(group.frames[index], group.frameTypes?.[index])
      if (!frame) return null
      frames.push(frame)
    }
    groups.push({ label: group.label, frames, start: group.start, end: group.end })
  }

  return {
    id: record.id,
    groups,
    totalProductCount: record.totalProductCount,
    transcript: record.transcript,
    ts: record.ts,
  }
}

export async function hasWhatshotTransfer(transferId: string): Promise<boolean> {
  const db = await openWhatshotDB()
  try {
    return Boolean(await db.get(WHATSHOT_TRANSFERS_STORE, transferId))
  } finally {
    await db.close()
  }
}

export async function saveWhatshotTransfer(
  payload: SaveWhatshotTransferPayload,
): Promise<void> {
  const groups = await Promise.all(
    payload.groups.map(async (group) => {
      const frames = await Promise.all(group.frames.map(toStorableBlobPayload))
      return {
        label: group.label,
        frames: frames.map(frame => frame.value),
        frameTypes: frames.map(frame => frame.type),
        start: group.start,
        end: group.end,
      }
    }),
  )

  const db = await openWhatshotDB()
  try {
    await db.put(WHATSHOT_TRANSFERS_STORE, {
      id: payload.transferId,
      groups,
      totalProductCount: payload.totalProductCount,
      transcript: payload.transcript,
      ts: payload.ts ?? Date.now(),
    })
  } finally {
    await db.close()
  }
}

/**
 * Atomically reads and removes a transfer. Generation is paid work, so a
 * refresh or second Batch tab must never claim the same payload twice.
 */
export async function claimWhatshotTransfer(
  transferId: string,
): Promise<WhatshotTransferRecord | null> {
  const db = await openWhatshotDB()
  try {
    const transaction = db.transaction(WHATSHOT_TRANSFERS_STORE, 'readwrite')
    const record = await transaction.store.get(transferId) as RawWhatshotTransferRecord | undefined
    if (record) await transaction.store.delete(transferId)
    await transaction.done
    return normalizeTransferRecord(record)
  } finally {
    await db.close()
  }
}
